import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useCreateDiveSite, useReverseGeocode } from '@/data/dive-sites';
import type { DiveSite } from '@/types/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { MapPicker } from './MapPicker';

interface QuickCreateDivesiteFormProps {
  initialQuery?: string;
  initialCoords?: { lat: number; lng: number } | null;
  onCreated: (site: DiveSite) => void;
  onCancel: () => void;
}

const SITE_TYPES: Array<{ value: string; label: string }> = [
  { value: '',         label: '–' },
  { value: 'reef',     label: 'Riff' },
  { value: 'wreck',    label: 'Wrack' },
  { value: 'wall',     label: 'Wand' },
  { value: 'cave',     label: 'Höhle' },
  { value: 'cenote',   label: 'Cenote' },
  { value: 'shore',    label: 'Shore' },
  { value: 'pinnacle', label: 'Pinnacle' },
  { value: 'drift',    label: 'Drift' },
  { value: 'lake',     label: 'See' },
  { value: 'other',    label: 'Sonstige' },
];

export function QuickCreateDivesiteForm({
  initialQuery = '',
  initialCoords = null,
  onCreated,
  onCancel,
}: QuickCreateDivesiteFormProps) {
  const [title, setTitle]     = useState(initialQuery);
  const [coords, setCoords]   = useState<{ lat: number; lng: number } | null>(initialCoords);
  const [region, setRegion]   = useState<string>('');
  const [siteType, setSiteType] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const create = useCreateDiveSite();
  const reverseGeocode = useReverseGeocode();

  // When coords are picked, ask the backend to reverse-geocode for a region hint.
  useEffect(() => {
    if (!coords) return;
    void reverseGeocode.mutateAsync({ lat: coords.lat, lon: coords.lng }).then((rg) => {
      if (rg.label && region === '') setRegion(rg.label);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coords?.lat, coords?.lng]);

  const onSubmit = async () => {
    if (!title.trim()) {
      toast.error('Titel ist Pflicht.');
      return;
    }
    if (!coords) {
      toast.error('Bitte Position auf der Karte wählen.');
      return;
    }

    setSubmitting(true);
    try {
      const site = await create.mutateAsync({
        title: title.trim(),
        geoLatitude:  coords.lat,
        geoLongitude: coords.lng,
        regionPath:   region ? region.split(',').map((s) => s.trim()).filter(Boolean) : undefined,
        siteType:     siteType || undefined,
      });
      toast.success('Tauchplatz angelegt.');
      onCreated(site);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Anlegen fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="qc-title">Name</Label>
        <Input id="qc-title" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
      </div>

      <div className="space-y-1.5">
        <Label>Position</Label>
        <MapPicker value={coords} onChange={(c) => setCoords(c)} height={260} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="qc-region">Region (optional, wird sonst aus Position geschätzt)</Label>
        <Input
          id="qc-region"
          placeholder="z.B. Egypt, Red Sea, Hurghada"
          value={region}
          onChange={(e) => setRegion(e.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="qc-type">Typ (optional)</Label>
        <select
          id="qc-type"
          className="h-10 w-full rounded-md border border-[var(--border)] bg-[var(--input)] px-3 text-sm"
          value={siteType}
          onChange={(e) => setSiteType(e.target.value)}
        >
          {SITE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>Abbrechen</Button>
        <Button type="button" onClick={onSubmit} disabled={submitting}>
          {submitting ? <Spinner /> : 'Speichern & verwenden'}
        </Button>
      </div>
    </div>
  );
}
